import { useState, useCallback, useMemo, useEffect } from 'react';
import { useCurrentRefinements, useInstantSearch } from 'react-instantsearch';

interface FilterState {
  [key: string]: boolean;
}

interface UseFilterStateProps {
  defaultOpenSections?: FilterState;
  autoCollapseOnMobile?: boolean;
}

interface UseFilterStateReturn {
  openSections: FilterState;
  toggleSection: (sectionId: string) => void;
  openSection: (sectionId: string) => void;
  closeSection: (sectionId: string) => void;
  openAllSections: () => void;
  closeAllSections: () => void;
  hasActiveRefinements: boolean;
  activeRefinementsCount: number;
  isLoading: boolean;
  resetSections: () => void;
}

// Default sections that should be open when no filters are applied
const DEFAULT_SECTIONS: FilterState = {
  status: true,
  category: false,
  brand: false,
  price: false,
  country: false,
  region: false,
  size: false,
  year: false,
};

// Mapping of filter sections to their corresponding attributes
const FILTER_SECTION_ATTRIBUTES: Record<string, string[]> = {
  status: ['hasBid', 'hasAsk'],
  category: ['category.name'],
  brand: ['brand.name'],
  price: ['lowestAsk'],
  country: ['brand.countryOfOrigin'],
  region: ['brand.region'],
  size: ['bottleSizeInMl'],
  year: ['distilledYear'],
};

/**
 * Custom hook for managing filter section state with dynamic behavior based on active refinements
 */
export default function useFilterState({
  defaultOpenSections = DEFAULT_SECTIONS,
  autoCollapseOnMobile = true,
}: UseFilterStateProps = {}): UseFilterStateReturn {
  // Get current refinements and search status
  const { items: currentRefinements } = useCurrentRefinements();
  const { status } = useInstantSearch();

  // Determine which sections have active refinements
  const sectionsWithActiveRefinements = useMemo(() => {
    const activeSections = new Set<string>();

    currentRefinements.forEach(refinement => {
      // Find which section this refinement belongs to
      Object.entries(FILTER_SECTION_ATTRIBUTES).forEach(([sectionId, attributes]) => {
        if (attributes.includes(refinement.attribute)) {
          activeSections.add(sectionId);
        }
      });
    });

    return activeSections;
  }, [currentRefinements]);

  // Calculate dynamic open sections based on active refinements
  const dynamicOpenSections = useMemo((): FilterState => {
    const hasActiveRefinements = currentRefinements.length > 0;

    if (!hasActiveRefinements) {
      // No filters applied - show default sections
      return defaultOpenSections;
    }

    // Filters applied - show default sections + sections with active refinements
    const newState = { ...defaultOpenSections };

    // Open sections that have active refinements
    sectionsWithActiveRefinements.forEach(sectionId => {
      if (sectionId in newState) {
        // eslint-disable-next-line security/detect-object-injection
        newState[sectionId] = true;
      }
    });

    return newState;
  }, [currentRefinements.length, sectionsWithActiveRefinements, defaultOpenSections]);

  const [openSections, setOpenSections] = useState<FilterState>(dynamicOpenSections);

  // Update open sections when dynamic state changes
  useEffect(() => {
    setOpenSections(dynamicOpenSections);
  }, [dynamicOpenSections]);

  // Auto-collapse on mobile if enabled
  useEffect(() => {
    if (!autoCollapseOnMobile || typeof window === 'undefined') return;

    const handleResize = () => {
      const isMobile = window.innerWidth < 768; // md breakpoint
      if (isMobile) {
        setOpenSections(prev => {
          const newState = { ...prev };
          // Keep only essential sections open on mobile, but preserve sections with active refinements
          Object.keys(newState).forEach(key => {
            if (!['status'].includes(key) && !sectionsWithActiveRefinements.has(key)) {
              if (key in newState) {
                // eslint-disable-next-line security/detect-object-injection
                newState[key] = false;
              }
            }
          });
          return newState;
        });
      }
    };

    // Check on mount
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [autoCollapseOnMobile, sectionsWithActiveRefinements]);

  // Memoized computed values
  const hasActiveRefinements = useMemo(
    () => currentRefinements.length > 0,
    [currentRefinements.length]
  );

  const activeRefinementsCount = useMemo(
    () => currentRefinements.length,
    [currentRefinements.length]
  );

  const isLoading = useMemo(() => status === 'loading' || status === 'stalled', [status]);

  // Section management functions
  const toggleSection = useCallback((sectionId: string) => {
    setOpenSections(prev => {
      if (sectionId in prev) {
        return {
          ...prev,
          // eslint-disable-next-line security/detect-object-injection
          [sectionId]: !prev[sectionId],
        };
      }
      return prev;
    });
  }, []);

  const openSection = useCallback((sectionId: string) => {
    setOpenSections(prev => {
      if (sectionId in prev) {
        return {
          ...prev,
          // eslint-disable-next-line security/detect-object-injection
          [sectionId]: true,
        };
      }
      return prev;
    });
  }, []);

  const closeSection = useCallback((sectionId: string) => {
    setOpenSections(prev => {
      if (sectionId in prev) {
        return {
          ...prev,
          // eslint-disable-next-line security/detect-object-injection
          [sectionId]: false,
        };
      }
      return prev;
    });
  }, []);

  const openAllSections = useCallback(() => {
    setOpenSections(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(key => {
        if (key in newState) {
          // eslint-disable-next-line security/detect-object-injection
          newState[key] = true;
        }
      });
      return newState;
    });
  }, []);

  const closeAllSections = useCallback(() => {
    setOpenSections(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(key => {
        if (key in newState) {
          // eslint-disable-next-line security/detect-object-injection
          newState[key] = false;
        }
      });
      return newState;
    });
  }, []);

  const resetSections = useCallback(() => {
    setOpenSections(dynamicOpenSections);
  }, [dynamicOpenSections]);

  return {
    openSections,
    toggleSection,
    openSection,
    closeSection,
    openAllSections,
    closeAllSections,
    hasActiveRefinements,
    activeRefinementsCount,
    isLoading,
    resetSections,
  };
}
